import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useStore, useDeliveryFacts } from "./StoreContext";
import { optionKind, optionValues, swatchFor, priceInfo } from "./shopData";
import { money, percentOff, dropDate } from "./format";
import { CountdownDigits, NotifyForm, AddToCalendar } from "./Countdown";
import ProductCard from "./ProductCard";
import Lightbox from "./Lightbox";
import Sheet from "./Sheet";
import { Select } from "./controls";
import Icon from "./Icons";
import useSeo, { absolute } from "./useSeo";
import { productLd, breadcrumbLd } from "./structuredData";

export default function ProductPage() {
  const { slug } = useParams();
  const { products, catalog, error, refresh } = useStore();
  const product = products.find((p) => p.slug === slug);

  if (!catalog) {
    return error ? (
      <div className="sh-wrap sh-empty">
        <p>The store didn't load. {error}</p>
        <button className="sh-btn" onClick={refresh}>
          Try again
        </button>
      </div>
    ) : (
      <ProductSkeleton />
    );
  }
  if (!product) return <NotFound />;
  return <Product key={product.id} product={product} />;
}

function NotFound() {
  useSeo({ title: "Not found", noindex: true });
  return (
    <div className="sh-wrap sh-empty">
      <p className="sh-eyebrow">404</p>
      <h1 className="sh-h1">This piece isn't in the store</h1>
      <p>It may have sold out for good or moved.</p>
      <Link className="sh-btn" to="/merch">
        See everything
      </Link>
    </div>
  );
}

// The variant whose options match the picked values (null until all picked).
function matchVariant(p, values) {
  if (!p.optionNames.length) return p.variants[0] || null;
  return p.variants.find((v) => p.optionNames.every((n) => v.optionValues?.[n] === values[n])) || null;
}

function initialValues(p) {
  // Single-value options are preselected; sizes wait for the customer
  // (preselecting a size is how people end up with the wrong one).
  const firstInStock = p.variants.find((v) => v.inStock) || p.variants[0];
  const values = {};
  for (const n of p.optionNames) {
    const vals = optionValues(p, n);
    if (vals.length === 1) values[n] = vals[0];
    else if (optionKind(p, n) !== "size" && firstInStock) values[n] = firstInStock.optionValues?.[n];
  }
  return values;
}

function Product({ product: p }) {
  const { products, lines, add, refresh } = useStore();
  const delivery = useDeliveryFacts();
  const [values, setValues] = useState(() => initialValues(p));
  const [lightbox, setLightbox] = useState(null);
  const [slide, setSlide] = useState(0);
  const [guide, setGuide] = useState(false);
  const [nudge, setNudge] = useState(false);
  const buyRef = useRef(null);
  const [buyVisible, setBuyVisible] = useState(true);
  const variant = matchVariant(p, values);
  const price = priceInfo(p);
  const shownPrice = variant ? variant.priceMinor : price.min;
  const was = variant ? (variant.compareAtMinor > variant.priceMinor ? variant.compareAtMinor : null) : price.was;
  const inCart = lines.find((l) => l.id === variant?.id)?.qty || 0;
  const full = variant && inCart >= (variant.maxQty ?? 0);
  const missing = p.optionNames.filter((n) => !values[n]);

  // Photos for the picked colour first, if it has its own.
  const images = useMemo(() => {
    const own = variant ? p.images.filter((i) => i.variantId === variant.id) : [];
    return own.length ? [...own, ...p.images.filter((i) => !own.includes(i))] : p.images;
  }, [p.images, variant]);
  useEffect(() => setSlide(0), [variant?.id]);

  // The mobile buy bar appears once the main button scrolls away.
  useEffect(() => {
    const el = buyRef.current;
    if (!el || !("IntersectionObserver" in window)) return undefined;
    const io = new IntersectionObserver(([e]) => setBuyVisible(e.isIntersecting || e.boundingClientRect.top > 0), { threshold: 0 });
    io.observe(el);
    return () => io.disconnect();
  }, []);

  useSeo({
    title: p.seo?.title || p.title,
    description: p.seo?.description || p.description || p.story,
    image: p.images[0]?.url,
    path: `/merch/${p.slug}`,
    type: "product",
    jsonLd: [productLd(p, { deliveryFromMinor: delivery.fromMinor, returnDays: delivery.returnDays }), breadcrumbLd(p)],
  });

  const onAdd = () => {
    if (missing.length) {
      setNudge(true);
      document.getElementById("sh-options")?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    if (variant?.inStock && !full) add(p, variant, 1);
  };

  const related = useMemo(() => {
    const score = (x) => (x.collection && x.collection.slug === p.collection?.slug ? 2 : 0) + (x.category?.slug === p.category?.slug ? 1 : 0) + (x.inStock ? 0.5 : 0);
    return products.filter((x) => x.id !== p.id).sort((a, b) => score(b) - score(a)).slice(0, 4);
  }, [products, p]);

  const details = Object.entries(p.metadata || {});
  const story = String(p.story || "").split(/\n\s*\n/).filter(Boolean);
  const sizeGuide = p.category?.sizeGuide?.length > 1 ? p.category.sizeGuide : null;

  let cta = "Add to cart";
  if (p.teased) cta = "Coming soon";
  else if (missing.length) cta = `Select ${missing[0].toLowerCase()}`;
  else if (!variant || !variant.inStock) cta = variant?.reserved ? "Reserved" : "Sold out";
  else if (full) cta = "All available are in your cart";
  const canBuy = !p.teased && (missing.length > 0 || (variant?.inStock && !full));

  return (
    <article className="sh-pdp">
      <div className="sh-wrap sh-pdp-grid">
        <div className="sh-gallery">
          {images.length ? (
            <>
              <div
                className="sh-gallery-track"
                onScroll={(e) => {
                  const el = e.currentTarget;
                  setSlide(Math.round(el.scrollLeft / el.clientWidth));
                }}
              >
                {images.map((im, i) => (
                  <button key={im.url} className="sh-gallery-item" onClick={() => setLightbox(i)} aria-label={`Open photo ${i + 1} of ${images.length}`}>
                    <img src={im.url} alt={im.alt || p.title} loading={i < 2 ? "eager" : "lazy"} decoding="async" fetchPriority={i === 0 ? "high" : undefined} />
                    <span className="sh-gallery-zoom" aria-hidden="true">
                      <Icon name="zoom" size={18} />
                    </span>
                  </button>
                ))}
              </div>
              {images.length > 1 && (
                <div className="sh-gallery-dots" aria-hidden="true">
                  {images.map((im, i) => (
                    <i key={im.url} className={i === slide ? "on" : ""} />
                  ))}
                </div>
              )}
            </>
          ) : (
            <div className="sh-gallery-empty">{p.title}</div>
          )}
          {p.teased && <div className="sh-gallery-teaser">More photos when it drops</div>}
        </div>

        <div className="sh-info">
          <div className="sh-info-sticky">
            <nav className="sh-crumbs" aria-label="Breadcrumb">
              <Link to="/merch">Merch</Link>
              {p.category && (
                <>
                  <span>/</span>
                  <Link to={`/merch?c=${p.category.slug}`}>{p.category.name}</Link>
                </>
              )}
            </nav>
            {p.collection && (
              <Link to={`/merch/collection/${p.collection.slug}`} className="sh-eyebrow sh-info-collection">
                {p.collection.name}
              </Link>
            )}
            <h1 className="sh-pdp-title">{p.title}</h1>
            <p className="sh-pdp-price">
              {!variant && price.from && <span className="sh-from">from </span>}
              <span className={was ? "sh-price-now" : ""}>{money(shownPrice)}</span>
              {was && (
                <>
                  <s>{money(was)}</s>
                  <span className="sh-badge sh-badge-sale sh-badge-inline">−{percentOff(shownPrice, was)} %</span>
                </>
              )}
            </p>
            <p className="sh-pdp-note">Final price{delivery.fromMinor != null ? ` · delivery from ${money(delivery.fromMinor)}` : ""}</p>

            {p.optionNames.length > 0 && (
              <div id="sh-options" className={`sh-options${nudge && missing.length ? " nudge" : ""}`}>
                {p.optionNames.map((name) => (
                  <OptionPicker
                    key={name}
                    p={p}
                    name={name}
                    values={values}
                    onPick={(v) => {
                      setValues({ ...values, [name]: v });
                      setNudge(false);
                    }}
                    onGuide={sizeGuide && optionKind(p, name) === "size" ? () => setGuide(true) : null}
                  />
                ))}
              </div>
            )}

            {p.teased ? (
              <div className="sh-dropbox">
                <p className="sh-eyebrow">Drops {dropDate(p.launchAt)} · Prague time</p>
                <CountdownDigits to={p.launchAt} onDone={refresh} />
                <NotifyForm topic={`drop:${p.collection?.slug || p.slug}`} cta="Notify me" compact />
                <AddToCalendar title={`The Creaks: ${p.title} drops`} iso={p.launchAt} url={absolute(`/merch/${p.slug}`)} />
              </div>
            ) : (
              <>
                <StockLine variant={variant} product={p} missing={missing.length > 0} />
                <div ref={buyRef}>
                  <button className="sh-btn sh-btn-xl" onClick={onAdd} disabled={!canBuy}>
                    {cta}
                    {canBuy && !missing.length && <span className="sh-btn-price">{money(shownPrice)}</span>}
                  </button>
                </div>
                {inCart > 0 && (
                  <p className="sh-incart">
                    <Icon name="check" size={14} /> {inCart} in your cart · <Link to="/merch/cart">View cart</Link>
                  </p>
                )}
                {variant && !variant.inStock && !missing.length && (
                  <div className="sh-restock">
                    <p>Want it when it's back?</p>
                    <NotifyForm topic={`restock:${p.slug}`} cta="Email me" compact done="Got it. We'll email you if it comes back." note={null} />
                  </div>
                )}
              </>
            )}

            <ul className="sh-assure">
              <li>
                <Icon name="box" size={18} /> Ships from Prague with Zásilkovna{delivery.freeOverMinor != null ? `, free over ${money(delivery.freeOverMinor)}` : ""}
              </li>
              <li>
                <Icon name="returns" size={18} /> {delivery.returnDays}-day returns
              </li>
              <li>
                <Icon name="lock" size={18} /> {delivery.card ? "Card, Apple Pay, Google Pay or bank transfer" : "Bank transfer with a QR code"}
              </li>
            </ul>

            <div className="sh-accordions">
              {p.description && (
                <Accordion title="Description" open>
                  <p className="sh-prose">{p.description}</p>
                </Accordion>
              )}
              {details.length > 0 && (
                <Accordion title="Details">
                  <dl className="sh-details">
                    {details.map(([k, v]) => (
                      <div key={k}>
                        <dt>{k}</dt>
                        <dd>{v}</dd>
                      </div>
                    ))}
                  </dl>
                </Accordion>
              )}
              {sizeGuide && (
                <Accordion title="Size guide">
                  <SizeTable rows={sizeGuide} />
                </Accordion>
              )}
              <Accordion title="Delivery and returns">
                <div className="sh-prose">
                  <p>
                    Sent from Prague with Zásilkovna (Packeta) to a pickup point, a Z-BOX or your door
                    {delivery.countries > 1 ? `, to ${delivery.countries} EU countries` : ""}. You get a tracking link as soon as it leaves.
                  </p>
                  <p>
                    You can return it within {delivery.returnDays} days of delivery, no reason needed. <Link to="/merch/returns">Returns</Link> ·{" "}
                    <Link to="/merch/terms">Terms</Link>
                  </p>
                </div>
              </Accordion>
            </div>
          </div>
        </div>
      </div>

      {story.length > 0 && (
        <section className="sh-story">
          <div className="sh-wrap sh-story-inner">
            <h2 className="sh-display sh-story-title">{p.title}</h2>
            <div className="sh-story-text">
              {story.map((para, i) => (
                <p key={i}>{para}</p>
              ))}
              {p.collection && (
                <Link className="sh-textlink" to={`/merch/collection/${p.collection.slug}`}>
                  More from {p.collection.name} <Icon name="arrow" size={16} />
                </Link>
              )}
            </div>
          </div>
        </section>
      )}

      {related.length > 0 && (
        <section className="sh-wrap sh-related" aria-label="You may also like">
          <h2 className="sh-h2">{p.collection && related.some((r) => r.collection?.slug === p.collection.slug) ? `More from ${p.collection.name}` : "You may also like"}</h2>
          <div className="sh-grid sh-grid-4">
            {related.map((r) => (
              <ProductCard key={r.id} product={r} />
            ))}
          </div>
        </section>
      )}

      {!p.teased && (
        <div className={`sh-buybar${buyVisible ? "" : " show"}`} aria-hidden={buyVisible}>
          <div className="sh-buybar-text">
            <strong>{p.title}</strong>
            <span>{money(shownPrice)}</span>
          </div>
          <button className="sh-btn" onClick={onAdd} disabled={!canBuy} tabIndex={buyVisible ? -1 : 0}>
            {missing.length ? `Select ${missing[0].toLowerCase()}` : cta}
          </button>
        </div>
      )}

      {lightbox != null && <Lightbox images={images} index={lightbox} onIndex={setLightbox} onClose={() => setLightbox(null)} />}
      <Sheet open={guide} onClose={() => setGuide(false)} title="Size guide">
        {sizeGuide && <SizeTable rows={sizeGuide} />}
        <p className="sh-muted sh-small">Measured flat. Between two sizes? Take the bigger one, or write to us.</p>
      </Sheet>
    </article>
  );
}

function OptionPicker({ p, name, values, onPick, onGuide }) {
  const kind = optionKind(p, name);
  const opts = optionValues(p, name);
  // Availability of each value given the other picked options.
  const stateOf = (o) => {
    const vs = p.variants.filter((v) => v.optionValues?.[name] === o && p.optionNames.every((n) => n === name || !values[n] || v.optionValues?.[n] === values[n]));
    if (!vs.length) return "none";
    return vs.some((v) => v.inStock) ? "in" : "out";
  };
  const picked = values[name];
  return (
    <div className="sh-opt" role="group" aria-labelledby={`opt-${name}`}>
      <div className="sh-opt-head">
        <span id={`opt-${name}`}>
          {name}
          {picked && kind !== "size" && <em>{picked}</em>}
        </span>
        {onGuide && (
          <button type="button" className="sh-textlink sh-opt-guide" onClick={onGuide}>
            <Icon name="ruler" size={16} /> Size guide
          </button>
        )}
      </div>
      {kind === "text" ? (
        <Select
          labelledBy={`opt-${name}`}
          label={name}
          placeholder={`Choose ${name.toLowerCase()}`}
          value={picked || ""}
          onChange={onPick}
          options={opts.map((o) => ({ value: o, label: o, hint: stateOf(o) === "out" ? "Sold out" : null }))}
        />
      ) : (
        <div className={kind === "colour" ? "sh-swatches" : "sh-sizegrid"}>
          {opts.map((o) => {
            const st = stateOf(o);
            const hex = kind === "colour" ? swatchFor(p, name, o) : null;
            return (
              <button
                key={o}
                type="button"
                className={`${kind === "colour" ? "sh-swatch" : "sh-size"}${picked === o ? " on" : ""}${st !== "in" ? " out" : ""}`}
                aria-pressed={picked === o}
                aria-label={`${name} ${o}${st !== "in" ? ", sold out" : ""}`}
                title={kind === "colour" ? o : undefined}
                onClick={() => onPick(o)}
                style={hex ? { "--sw": hex } : undefined}
              >
                {kind === "colour" ? <i /> : o}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function StockLine({ variant, product, missing }) {
  if (missing) {
    const any = product.variants.some((v) => v.inStock);
    return <p className="sh-stock">{any ? <><i className="ok" /> In stock</> : <><i className="out" /> Sold out</>}</p>;
  }
  if (!variant) return <p className="sh-stock"><i className="out" /> Not available in this combination</p>;
  if (variant.inStock && variant.lowStock) return <p className="sh-stock low"><i className="low" /> Only {variant.lowStock} left</p>;
  if (variant.inStock) return <p className="sh-stock"><i className="ok" /> In stock, ready to ship</p>;
  if (variant.reserved) return <p className="sh-stock"><i className="low" /> Held in someone's checkout. It may come back if they don't pay.</p>;
  return <p className="sh-stock"><i className="out" /> Sold out</p>;
}

function Accordion({ title, open, children }) {
  return (
    <details className="sh-acc" open={open}>
      <summary>
        {title}
        <Icon name="plus" size={16} />
      </summary>
      <div className="sh-acc-body">{children}</div>
    </details>
  );
}

function SizeTable({ rows }) {
  return (
    <div className="sh-tablewrap">
      <table className="sh-table">
        <thead>
          <tr>
            {rows[0].map((c, i) => (
              <th key={i}>{c}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.slice(1).map((r, i) => (
            <tr key={i}>
              {r.map((c, j) => (j === 0 ? <th key={j}>{c}</th> : <td key={j}>{c}</td>))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ProductSkeleton() {
  return (
    <div className="sh-wrap sh-pdp-grid sh-skel" aria-hidden="true">
      <div className="sh-gallery">
        <div className="sh-gallery-item" />
      </div>
      <div className="sh-info">
        <span className="sh-skel-line" />
        <span className="sh-skel-line" />
        <span className="sh-skel-line short" />
      </div>
    </div>
  );
}
